import { axiosClient } from './axiosClient';
import { ENDPOINTS } from './endpoints';
import { getErrorMessage } from './errors';

function mapApiUser(apiUser) {
  const toId = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    return v?.toString?.() ?? String(v);
  };
  return {
    id: apiUser.id || apiUser._id,
    email: apiUser.email,
    name: apiUser.name,
    role: apiUser.role,
    createdAt: Date.now(),
    mustChangePassword: !!apiUser.mustChangePassword,
    avatarUrl: apiUser.avatarUrl || '',
    linkedStudentIds: (apiUser.linkedStudentIds || []).map(toId).filter(Boolean),
    linkedParentIds: (apiUser.linkedParentIds || []).map(toId).filter(Boolean),
  };
}

export const authService = {
  async getMe() {
    try {
      const { data } = await axiosClient.get(ENDPOINTS.auth.me, { skipGlobalLoader: true });
      const raw = data?.data ?? data;
      return mapApiUser(raw);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Session expired'));
    }
  },

  async revealCredential(token) {
    try {
      const { data } = await axiosClient.get(ENDPOINTS.auth.revealCredential, {
        params: { token },
        skipAuth: true,
        skipGlobalLoader: true,
      });
      const raw = data?.data ?? data;
      return {
        email: raw.email,
        tempPassword: raw.tempPassword,
        name: raw.name,
        pending: !!raw.pending,
        redirectToLogin: !!raw.redirectToLogin,
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load credentials'));
    }
  },

  async login(payload) {
    try {
      const { data } = await axiosClient.post(ENDPOINTS.auth.login, payload, {
        skipAuth: true,
      });
      const apiUser = data.user;
      if (apiUser.token) {
        try {
          window.localStorage.setItem('auth-token', apiUser.token);
        } catch {
          // Ignore storage failures.
        }
      }
      return mapApiUser(apiUser);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Login failed'));
    }
  },

  async forgotPassword(payload) {
    try {
      await axiosClient.post(ENDPOINTS.auth.forgotPassword, payload, {
        skipAuth: true,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to request password reset'));
    }
  },

  async resetPassword(payload) {
    try {
      await axiosClient.post(ENDPOINTS.auth.resetPassword, payload, {
        skipAuth: true,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to reset password'));
    }
  },

  async changePassword(payload) {
    try {
      await axiosClient.post(ENDPOINTS.auth.changePassword, payload);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to change password'));
    }
  },
};
